// The one Add-reminder form, used everywhere a reminder is added (Reminders page, an activity, later Today).
// Fill it by hand, or speak (recorded in the browser, transcribed on the server) or describe it in words: the
// assistant fills the fields, which stay tinted violet until you change them. Nothing is saved until Save,
// and Save creates the reminder directly: the form is the review, so there is no approval card.
import { useMemo, useState, type FormEvent } from 'react';
import type { ReminderDraft, SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useChange, useOpenActivities, useTeammates } from '../lib/queries';
import { localDate, toIsoAt } from '../lib/format';
import { MAX_SECONDS, canRecord, useRecorder } from '../lib/recorder';
import { ErrorNote } from './bits';
import { useToast } from './toast';

type Field = 'text' | 'date' | 'time' | 'forUserId' | 'activityId';

export function AddReminderForm({ me, activityId: fixedActivity, onSaved, autoFocus, layout = 'inline', phone = false, onCancel }: {
  me: SessionUser; activityId?: string; onSaved?: () => void; autoFocus?: boolean;
  /** modal: in a dialog or sheet (Reminders' Add reminder): What, then When, then For and Linked to, Cancel and Save. */
  layout?: 'inline' | 'modal'; phone?: boolean; onCancel?: () => void;
}) {
  const modal = layout === 'modal';
  const toast = useToast();
  const people = useTeammates();
  const acts = useOpenActivities();
  const [text, setText] = useState('');
  const [date, setDate] = useState(localDate());
  const [time, setTime] = useState('09:00');
  const [forId, setForId] = useState(me.id);
  const [activityId, setActivityId] = useState(fixedActivity ?? '');
  const [ai, setAi] = useState<Set<Field>>(new Set());
  const [words, setWords] = useState('');
  const [notes, setNotes] = useState<string[]>([]);
  const [demoNote, setDemoNote] = useState('');

  const touched = (f: Field) => setAi(s => { if (!s.has(f)) return s; const n = new Set(s); n.delete(f); return n; });
  const tint = (f: Field) => (ai.has(f) ? ' aifill' : '');

  const apply = (d: ReminderDraft) => {
    if (d.text) setText(d.text);
    if (d.date) setDate(d.date);
    setTime(d.time ?? (d.date ? '' : time));
    if (d.forUserId) setForId(d.forUserId);
    if (d.activityId && !fixedActivity) setActivityId(d.activityId);
    setAi(new Set(d.filled.filter(f => f !== 'activityId' || !fixedActivity)));
    setNotes(d.notes);
  };
  const parse = useChange((t: string) => api.reminders.parse(t));
  const fill = (t: string) => parse.mutate(t, { onSuccess: apply });

  const save = useChange(() => api.reminders.create({
    text: text.trim(), forUserId: forId === me.id ? null : forId, activityId: activityId || null,
    ...(time ? { remindAt: toIsoAt(date, time) } : { remindDate: date }),
  }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim() || !date) return;
    save.mutate(undefined, {
      onSuccess: r => {
        toast(r.setByOther ? `Set for ${r.forUser.name}` : 'Reminder set');
        setText(''); setWords(''); setNotes([]); setAi(new Set()); setForId(me.id); if (!fixedActivity) setActivityId('');
        onSaved?.();
      },
    });
  };

  // ---------- Speak ----------
  // The shared dictation: a pause of about two seconds ends it, and the words fill the form (Fill in while
  // recording does the same at once). Silence or noise is "Didn't catch that", and nothing is filled.
  const recorder = useRecorder((t, demo) => {
    setWords(t);
    setDemoNote(demo ? 'Voice is not connected here, so a sample sentence was used.' : '');
    fill(t);
  });
  const rec = recorder.state, seconds = recorder.seconds;
  const recErr = recorder.error || demoNote;
  const start = () => { setDemoNote(''); void recorder.start(); };
  const stop = () => recorder.stop();

  const groups = useMemo(() => {
    const m = new Map<string, { id: string; title: string }[]>();
    for (const a of acts.data ?? []) m.set(a.spaceName, [...(m.get(a.spaceName) ?? []), { id: a.id, title: a.title }]);
    return [...m.entries()];
  }, [acts.data]);
  const fixedTitle = fixedActivity ? acts.data?.find(a => a.id === fixedActivity)?.title : null;
  const err = save.error ?? parse.error;

  return (
    <form className={`remform${modal ? ' modal' : ' panel'}${phone ? ' phone' : ''}`} onSubmit={submit} aria-label="Add a reminder">
      <div className="remwords">
        {canRecord() && (
          rec === 'recording'
            ? <button type="button" className="bt rec on" onClick={stop} aria-label="Stop recording"><span className="dot" />Stop ({MAX_SECONDS - seconds}s)</button>
            : <button type="button" className="bt rec" onClick={start} disabled={rec !== 'idle' || parse.isPending} aria-label="Speak the reminder">
              {rec === 'working' || rec === 'asking' ? <span className="spin" /> : <span className="aiorb" />}{rec === 'working' ? 'Listening back' : 'Speak'}
            </button>
        )}
        <input className="inp" value={words} onChange={e => setWords(e.target.value)} placeholder='Or describe it: "remind Sara Thursday at 10 to send the deck"' aria-label="Or describe it"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (words.trim()) fill(words.trim()); } }} />
        <button type="button" className="bt" disabled={parse.isPending || rec === 'working' || (!words.trim() && rec !== 'recording')} onClick={() => (rec === 'recording' ? recorder.stop({ send: true }) : fill(words.trim()))}>{parse.isPending ? <span className="spin" /> : null}Fill in</button>
      </div>
      {recErr && <div className="xs mut" role="status">{recErr}</div>}
      {notes.length > 0 && <div className="xs mut" role="status">{notes.join(' ')}</div>}
      <div className="remgrid">
        <label className="fld wide">What<input className={`inp${tint('text')}`} value={text} onChange={e => { setText(e.target.value); touched('text'); }} placeholder="Send Dr. Amir the steering agenda" required maxLength={500} autoFocus={autoFocus} /></label>
        <label className="fld">Date<input className={`inp${tint('date')}`} type="date" value={date} onChange={e => { setDate(e.target.value); touched('date'); }} required /></label>
        <label className="fld">Time (optional)<input className={`inp${tint('time')}`} type="time" value={time} onChange={e => { setTime(e.target.value); touched('time'); }} /></label>
        <label className="fld">For<select className={`inp${tint('forUserId')}`} value={forId} onChange={e => { setForId(e.target.value); touched('forUserId'); }}>
          <option value={me.id}>Me</option>
          {(people.data ?? []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select></label>
        <label className="fld wide linked">Linked to
          {fixedActivity
            ? <input className="inp" value={fixedTitle ?? 'This activity'} readOnly aria-readonly="true" />
            : (
              <select className={`inp${tint('activityId')}`} value={activityId} onChange={e => { setActivityId(e.target.value); touched('activityId'); }}>
                <option value="">Nothing in particular</option>
                {groups.map(([space, list]) => <optgroup key={space} label={space}>{list.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}</optgroup>)}
              </select>
            )}
        </label>
        {!modal && <button className="bt pri" disabled={save.isPending || !text.trim() || rec === 'recording' || rec === 'working'}>{save.isPending ? <span className="spin" /> : null}Save</button>}
      </div>
      {!modal && ai.size > 0 && <div className="xs mut"><span className="aifillkey" /> Filled in by the assistant. Check them, then Save.</div>}
      {modal && (
        <div className="remfoot">
          <span className="xs mut">{ai.size > 0 ? <><span className="aifillkey" /> Filled in by the assistant. Check them, then save.</> : 'Nothing is saved until you save it.'}</span>
          {onCancel && <button type="button" className={phone ? 'ph-btf ghost' : 'bt'} onClick={onCancel}>Cancel</button>}
          <button className={phone ? 'ph-btf' : 'bt pri'} disabled={save.isPending || !text.trim() || rec === 'recording' || rec === 'working'}>{save.isPending ? <span className="spin" /> : null}Save reminder</button>
        </div>
      )}
      {err ? <ErrorNote error={err} /> : null}
    </form>
  );
}
